import { Button, mergeClasses } from "@fluentui/react-components";
import { AddRegular, DeleteRegular, InfoRegular } from "@fluentui/react-icons";
import {
  useClientUsers,
  useInviteClientUser,
  useRemoveClientUser,
} from "apps/project-iq/hooks/useClientUsers";
import { IClientUser } from "apps/project-iq/models/IClientUser";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { AdminListPage } from "components/adminList/AdminListPage";
import { IAdminRowAction } from "components/adminList/AdminListPage.types";
import { useAdminColumnStyles } from "components/adminList/AdminTable.styles";
import { ITableColumn } from "models/ITableColumn";
import { useMemo, useState } from "react";
import { InviteClientDialog } from "./InviteClientDialog";
import { useWorkspaceClientInvitationsStyles } from "./WorkspaceClientInvitations.styles";
import { IWorkspaceClientInvitationsProps } from "./WorkspaceClientInvitations.types";

const ACTIONS_COLUMN_WIDTH = "96px";
const SECONDS_TO_MILLISECONDS = 1000;
const SECONDS_IN_A_DAY = 86400;
const EMPTY_VALUE = "—";
const BLANK_VALUE = "";
const ACCOUNT_RETENTION_IN_DAYS = 30;

type ClientStatus = "unknown" | "revoked" | "expired" | "pending" | "active";

const STATUS_LABELS: Record<ClientStatus, string> = {
  unknown: "Unknown",
  revoked: "Revoked",
  expired: "Expired",
  pending: "Pending",
  active: "Active",
};

const STATUS_DESCRIPTIONS: Record<ClientStatus, string> = {
  unknown: "Their Azure AD account could not be read",
  revoked: "Their access to this workspace has been removed",
  expired: `They did not sign in within ${ACCOUNT_RETENTION_IN_DAYS} days of being invited`,
  pending: "Invited, but they have not signed in yet",
  active: "They have signed in at least once",
};

// Sorts unknown below the statuses an admin can act on, so the rows that need
// attention stay together at one end of the column.
const STATUS_ORDER: Record<ClientStatus, number> = {
  unknown: 0,
  revoked: 1,
  expired: 2,
  pending: 3,
  active: 4,
};

const formatDateTime = (timestampInSeconds?: number | null): string =>
  timestampInSeconds
    ? new Date(timestampInSeconds * SECONDS_TO_MILLISECONDS).toLocaleString()
    : EMPTY_VALUE;

const formatLastLogin = (timestampInSeconds?: number | null): string =>
  timestampInSeconds ? formatDateTime(timestampInSeconds) : BLANK_VALUE;

const isInviteExpired = (invitedAtInSeconds?: number | null): boolean => {
  if (!invitedAtInSeconds) return false;
  const expiresAtInSeconds =
    invitedAtInSeconds + ACCOUNT_RETENTION_IN_DAYS * SECONDS_IN_A_DAY;
  return expiresAtInSeconds * SECONDS_TO_MILLISECONDS < Date.now();
};

// The backend reports the limit as "All N client slots are full. ..."
const CLIENT_LIMIT_ERROR_PATTERN = /All (\d+) client slots are full/i;

const getMaximumClientsFromError = (error: unknown): string | null => {
  const message = error instanceof Error ? error.message : "";
  return CLIENT_LIMIT_ERROR_PATTERN.exec(message)?.[1] ?? null;
};

const getClientStatus = (client: IClientUser): ClientStatus => {
  if (client.isActive === null || client.isActive === undefined)
    return "unknown";
  if (!client.isActive) return "revoked";
  if (client.lastActivityDate) return "active";
  return isInviteExpired(client.createdAt) ? "expired" : "pending";
};

export const WorkspaceClientInvitations = ({
  workspaceId,
}: IWorkspaceClientInvitationsProps): JSX.Element => {
  const styles = useWorkspaceClientInvitationsStyles();
  const columnStyles = useAdminColumnStyles();
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [inviteErrorMessage, setInviteErrorMessage] = useState("");
  const [removalErrorMessage, setRemovalErrorMessage] = useState("");
  const [removalPendingClient, setRemovalPendingClient] =
    useState<IClientUser | null>(null);

  const statusFlagStyles = useMemo(
    () => ({
      revoked: styles.activityFlagRevoked,
      expired: styles.activityFlagExpired,
      pending: styles.activityFlagPending,
      active: styles.activityFlagActive,
    }),
    [styles],
  );

  const { data: clientUsers, isLoading, isError } = useClientUsers(workspaceId);
  const { mutateAsync: inviteClient, isLoading: isInviting } =
    useInviteClientUser(workspaceId);
  const { mutateAsync: removeClient } = useRemoveClientUser(workspaceId);

  const handleOpenInviteDialog = () => {
    setInviteErrorMessage("");
    setIsInviteDialogOpen(true);
  };

  const handleInvite = async (
    email: string,
    displayName?: string,
    message?: string,
  ) => {
    try {
      await inviteClient({ email, displayName, message });
      setIsInviteDialogOpen(false);
    } catch (error) {
      const maximumClients = getMaximumClientsFromError(error);
      setInviteErrorMessage(
        maximumClients
          ? `Client invite limit reached. This workspace already has the maximum of ${maximumClients} clients. Remove an existing client before inviting another.`
          : "We could not send that invitation. Please try again.",
      );
    }
  };

  const handleConfirmRemoval = async () => {
    if (!removalPendingClient) return;
    setRemovalErrorMessage("");
    try {
      await removeClient(removalPendingClient.id);
    } catch {
      setRemovalErrorMessage(
        `We could not remove ${removalPendingClient.email}. Please try again.`,
      );
    } finally {
      setRemovalPendingClient(null);
    }
  };

  const columns = useMemo<ITableColumn<IClientUser>[]>(
    () => [
      {
        columnId: "email",
        displayValue: "Email",
        className: columnStyles.wide,
        compare: (first, second) => first.email.localeCompare(second.email),
        renderHeaderCell: () => "Email",
        renderCell: (item) => item.email,
      },
      {
        columnId: "displayName",
        displayValue: "Name",
        className: columnStyles.normal,
        compare: (first, second) =>
          (first.displayName ?? "").localeCompare(second.displayName ?? ""),
        renderHeaderCell: () => "Name",
        renderCell: (item) => item.displayName || EMPTY_VALUE,
      },
      {
        columnId: "invitedByName",
        displayValue: "Added by",
        className: columnStyles.normal,
        compare: (first, second) =>
          (first.invitedByName ?? "").localeCompare(second.invitedByName ?? ""),
        renderHeaderCell: () => "Added by",
        renderCell: (item) => item.invitedByName || EMPTY_VALUE,
      },
      {
        columnId: "createdAt",
        displayValue: "Invited on",
        className: columnStyles.normal,
        compare: (first, second) =>
          (first.createdAt ?? 0) - (second.createdAt ?? 0),
        renderHeaderCell: () => "Invited on",
        renderCell: (item) => formatDateTime(item.createdAt),
      },
      {
        columnId: "lastActivityDate",
        displayValue: "Last login",
        className: columnStyles.normal,
        compare: (first, second) =>
          (first.lastActivityDate ?? 0) - (second.lastActivityDate ?? 0),
        renderHeaderCell: () => "Last login",
        renderCell: (item) => formatLastLogin(item.lastActivityDate),
      },
      {
        columnId: "status",
        displayValue: "Status",
        className: columnStyles.narrow,
        compare: (first, second) =>
          STATUS_ORDER[getClientStatus(first)] -
          STATUS_ORDER[getClientStatus(second)],
        renderHeaderCell: () => "Status",
        renderCell: (item) => {
          const status = getClientStatus(item);
          if (status === "unknown") {
            return (
              <span title={STATUS_DESCRIPTIONS.unknown}>
                {STATUS_LABELS.unknown}
              </span>
            );
          }
          return (
            <span
              className={mergeClasses(
                styles.activityFlag,
                statusFlagStyles[status],
              )}
              title={STATUS_DESCRIPTIONS[status]}
            >
              <span className={styles.activityDot} aria-hidden="true" />
              {STATUS_LABELS[status]}
            </span>
          );
        },
      },
    ],
    [columnStyles, statusFlagStyles, styles],
  );

  const rowActions = useMemo<IAdminRowAction<IClientUser>[]>(
    () => [
      {
        icon: <DeleteRegular aria-hidden="true" />,
        tooltip: "Remove access",
        onClick: (item) => {
          setRemovalErrorMessage("");
          setRemovalPendingClient(item);
        },
      },
    ],
    [],
  );

  return (
    <div className={styles.page}>
      <div className={styles.pageContent}>
        <div className={styles.header}>
          <div className={styles.headerText}>
            <h2 className={styles.sectionTitle}>Client invitations</h2>
            <p className={styles.sectionDescription}>
              Clients invited here can sign in to the client view and read the
              deliverables and documents shared with them.
            </p>
          </div>
          <Button
            appearance="primary"
            icon={<AddRegular aria-hidden="true" />}
            onClick={handleOpenInviteDialog}
            aria-label="Invite client"
          >
            Invite client
          </Button>
        </div>

        <p className={styles.retentionNote}>
          <InfoRegular
            className={styles.retentionNoteIcon}
            aria-hidden="true"
          />
          <span>
            Clients who do not sign in within {ACCOUNT_RETENTION_IN_DAYS} days
            of being invited have their accounts deleted.
          </span>
        </p>

        {removalErrorMessage && (
          <span role="alert" className={styles.errorMessage}>
            {removalErrorMessage}
          </span>
        )}

        <div className={styles.adminListWrapper}>
          <AdminListPage
            items={clientUsers ?? []}
            columns={columns}
            rowActions={rowActions}
            isLoading={isLoading}
            getRowId={(item) => item.id}
            getRowLabel={(item) => item.email}
            emptyStateMessage={
              isError
                ? "We could not load the invited clients. Please refresh to try again."
                : "No clients invited yet. Invite a client to give them access to this workspace."
            }
            actionsWidth={ACTIONS_COLUMN_WIDTH}
            actionsHeaderLabel=""
          />
        </div>
      </div>

      <InviteClientDialog
        isOpen={isInviteDialogOpen}
        isInviting={isInviting}
        errorMessage={inviteErrorMessage}
        onDismiss={() => setIsInviteDialogOpen(false)}
        onConfirm={handleInvite}
      />

      <ActionDialog
        isVisible={removalPendingClient !== null}
        title="Remove client access?"
        message={
          removalPendingClient
            ? `${removalPendingClient.email} will lose access to this workspace. Their account and access to other workspaces are not affected.`
            : "This client will lose access to this workspace."
        }
        confirmationMessage="Remove"
        dismissMessage="Cancel"
        spinnerMessage="Removing..."
        onConfirmation={handleConfirmRemoval}
        onDismiss={() => setRemovalPendingClient(null)}
      />
    </div>
  );
};
